<script>
  import { getContext, onMount } from "svelte";
  import { useParams } from "svelte-navigator";
  import { useMyProjectPermission } from "../../../hooks/useMyProjectPermission.js";
  import { useProject } from "../../../hooks/useProject.js";
  import PrimaryBorderedButton from "../../../components/button/generic/PrimaryBorderedButton.svelte";
  import { usePageContext } from "../../../components/pages/pageContext.js";
  import Loading from "../../../components/pages/Loading.svelte";
  import { translate } from "../../../i18n/i18next.js";
  import { navigateI18n } from "../../../i18n/navigate.js";
  import Alert from "../../../components/Alerts/Alert.svelte";
  import { useEnabledExperimentalViews } from "../../../hooks/useEnabledExperimentalViews.js";
  import DispatchingViewViewCard from "../../../components/views/cards/DispatchingViewViewCard.svelte";
  import ResGanttViewCard from "../../../components/views/cards/ResGanttViewCard.svelte";
  import OrderGanttViewCard from "../../../components/views/cards/OrderGanttViewCard.svelte";
  import OrderLeadTimeViewCard from "../../../components/views/cards/OrderLeadTimeViewCard.svelte";
  import OperationTableViewCard from "../../../components/views/cards/OperationTableViewCard.svelte";
  import IntegratedMasterViewCard from "../../../components/views/cards/IntegratedMasterViewCard.svelte";
  import { useTimezone } from "../../../hooks/useTimezone.js";
  import ViewCardBase from "../../../components/views/cards/ViewCardBase.svelte";
  import OrderTableViewCard from "../../../components/views/cards/OrderTableViewCard.svelte";
  import CustomizedResGanttViewCard from "../../../components/views/cards/CustomizedResGanttViewCard.svelte";

  const params = useParams();
  const projectId = $params.project;
  const {getOffset} = useTimezone()
  const { setHeaderProps } = usePageContext();
  $: setHeaderProps({ title: $project?.name ?? translate("generic.project") });

  const { handleFetchErrors } = getContext("fetchErrorHandler");
  const { permission } = useMyProjectPermission(projectId);
  const project = useProject(projectId, handleFetchErrors);
  const enabledExperimentalViews = useEnabledExperimentalViews()

  const experimentalViews = {
    ExpectedActualCmp: {
      iconSrc: "/assets/icons/icon_expected_actual_cmp.png",
      href: `/projects/${projectId}/ExpectedActualCmp`,
      name: {
        ja: "工程毎予実比較",
      },
    },
    LoadGraph: {
      iconSrc: "/assets/icons/icon_load_graph_l.png",
      href: `/projects/${projectId}/LoadGraph`,
      name: {
        ja: "負荷率グラフ",
      },
    },
    LoadGraph_MPS: {
      iconSrc: "/assets/icons/icon_load_graph_mps.png",
      href: `/projects/${projectId}/LoadGraph_MPS`,
      name: {
        ja: "負荷グラフ (MPS)",
      },
    },
    PeggedOrderGantt: {
      iconSrc: "/assets/icons/icon_temp.png",
      href: `/projects/${projectId}/PeggedOrderGantt`,
      name: {
        ja: "紐付けオーダガント",
      },
    },
    ProductionOrder: {
      iconSrc: "/assets/icons/icon_production_order_l.png",
      href: `/projects/${projectId}/ProductionOrder`,
      name: {
        ja: "生産指示書",
      },
    },
    PSIGraph: {
      iconSrc: "/assets/icons/icon_psi_graph.png",
      href: `/projects/${projectId}/PSIGraph`,
      name: {
        ja: "PSIグラフ",
      },
    }, PSITable: {
      iconSrc: "/assets/icons/icon_temp.png",
      href: `/projects/${projectId}/PSITable`,
      name: {
        ja: "PSI表",
      },
    },
    PSITable_MPS: {
      iconSrc: "/assets/icons/icon_psi_table_mps.png",
      href: `/projects/${projectId}/PSITable_MPS`,
      name: {
        ja: "PSI表 (MPS)",
      },
    },
    ResultGraph: {
      iconSrc: "/assets/icons/icon_result_graph_l.png",
      href: `/projects/${projectId}/ResultGraph`,
      name: {
        ja: "実績グラフ",
      },
    },
    SimpleOrderGantt: {
      iconSrc: "/assets/icons/icon_simple_order_gantt_l.png",
      href: `/projects/${projectId}/SimpleOrderGantt`,
      name: {
        ja: "シンプル版オーダガントチャート",
      },
    },
    SimpleResGantt: {
      iconSrc: "/assets/icons/icon_simple_res_gantt_l.png",
      href: `/projects/${projectId}/SimpleResGantt`,
      name: {
        ja: "シンプル版資源ガントチャート",
      },
    },
  }

  const timezoneOffset = new Date().getTimezoneOffset() * -60
  $: projectTimezoneOffset = $getOffset($project?.timezone)

  $: isTimezoneDifferent = projectTimezoneOffset && timezoneOffset !== projectTimezoneOffset

  let myViews = {status: "loading"}
  onMount(async () => {
    try {
      const data = await fetch(`/api/me/projects/${projectId}/views`).then(res => res.json())
      myViews = {status: "loaded", views: data.views}
    } catch (e) {
      console.log(e)
      myViews = {status: "error"}
    }
  })
</script>

<main>
  <div id="content-container">
    {#if isTimezoneDifferent}
      <Alert type="warn">
        <span style="font-size: 0.8rem">
          {translate("frontend:pages:projects.project.index.differentTimezoneWarning", null, {timezone: $project.timezone})}
        </span>
      </Alert>
    {/if}
    {#if $permission === "ProjectAdmin" || $permission === "ServerAdmin"}
      <div id="top-button-container">
        <PrimaryBorderedButton onClick={() => navigateI18n(`/projects/${projectId}/admin`)}>
          <span style="display: inline-flex; align-items: center; gap: 1rem;">
            <img src="/assets/icons/icon_setting_blue.png" alt="settings icon" class="button-icon"/>
            {translate("frontend:pages:projects.project.index.settingsButtonLabel")}
          </span>
        </PrimaryBorderedButton>
      </div>
    {/if}
    <div id="view-button-cards-container">
      {#if myViews.status === "loaded"}
        {#each myViews.views as viewName}
          {#if viewName === "DispatchingView"}
            <DispatchingViewViewCard
              href={`/projects/${projectId}/DispatchingView`}
            />
          {/if}
          {#if viewName === "ResGantt"}
            <ResGanttViewCard
              href={`/projects/${projectId}/ResGantt`}
            />
          {/if}
          {#if viewName === "CustomizedResGantt"}
            <CustomizedResGanttViewCard
              href={`/projects/${projectId}/CustomizedResGantt`}
            />
          {/if}
          {#if viewName === "OrderGantt"}
            <OrderGanttViewCard
              href={`/projects/${projectId}/OrderGantt`}
            />
          {/if}
          {#if viewName === "OrderLeadTime"}
            <OrderLeadTimeViewCard
              href={`/projects/${projectId}/OrderLeadTime`}
            />
          {/if}
          {#if viewName === "OrderTable"}
            <OrderTableViewCard
              href={`/projects/${projectId}/OrderTable`}
            />
          {/if}
          {#if viewName === "OperationTable"}
            <OperationTableViewCard
              href={`/projects/${projectId}/OperationTable`}
            />
          {/if}
          {#if viewName === "IntegratedMaster"}
            <IntegratedMasterViewCard
              onClick={() => navigateI18n(`/projects/${projectId}/IntegratedMaster`)}
              href={`/projects/${projectId}/IntegratedMaster`}
            />
          {/if}
          {#if experimentalViews[viewName] && $enabledExperimentalViews.includes(viewName)}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <ViewCardBase
              viewIconSrc={experimentalViews[viewName].iconSrc}
              href={experimentalViews[viewName].href}
            >
              <span>{@html experimentalViews[viewName].name.ja}</span>
            </ViewCardBase>
          {/if}
        {:else}
          <div>{translate("frontend:pages:projects.project.index.noUiWarning")}</div>
        {/each}
      {:else}
        <Loading/>
      {/if}
    </div>
  </div>
</main>


<style>
  main {
    height: 100%;
    background-color: white;
  }
  #top-button-container {
    display: flex;
    justify-content: end;
    align-items: center;
    margin-bottom: 1rem;
  }
  #content-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
  .button-icon {
    height: 1rem;
    aspect-ratio: 1;
  }
  #view-button-cards-container {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
  }
</style>
